/**
 * Editable attendance row (status + absence reason) for a session participant.
 */

import * as React from 'react';
import { Avatar } from './Avatar';
import { Select, SelectOption } from '@/components/forms/Select';
import { Input } from '@/components/forms/Input';
import {
  ATTENDANCE_STATUS_LABELS,
  TUTOR_ABSENCE_REASONS,
  STUDENT_ABSENCE_REASONS,
} from '@/constants/shared-catalog';
import type {
  AttendanceStatus,
  AbsenceReason,
  ParticipantAttendance,
} from '@/types/attendance.types';
import type { SessionParticipant } from '@/types/inperson/session.types';
import { getRoleLabel } from '@/utils/role-label-utils';

/** Statuses shown when the caller does not restrict the control. */
const DEFAULT_ATTENDANCE_STATUSES: readonly AttendanceStatus[] = [
  'present',
  'absent',
  'excused',
  'pending',
];

/** Props for {@link AttendanceRow}. */
interface AttendanceRowProps {
  participant: SessionParticipant;
  value: ParticipantAttendance;
  onChange: (value: ParticipantAttendance) => void;
  error?: string;
  /**
   * Statuses the user can pick. A stored status outside this list stays visible
   * and is saved unchanged, but it is not offered in the menu.
   */
  offeredStatuses?: readonly AttendanceStatus[];
}

/** Presence options. A stored status outside `offeredStatuses` is kept for display only. */
function attendanceStatusOptions(
  current: AttendanceStatus,
  offeredStatuses?: readonly AttendanceStatus[],
): SelectOption[] {
  const offered = offeredStatuses ?? DEFAULT_ATTENDANCE_STATUSES;
  const options: SelectOption[] = offered.map((status) => ({
    value: status,
    label: ATTENDANCE_STATUS_LABELS[status],
  }));
  if (!offered.includes(current)) {
    options.push({
      value: current,
      label: ATTENDANCE_STATUS_LABELS[current],
      disabled: true,
      hidden: true,
    });
  }
  return options;
}

/** First letters of first + last name, or `?` when both are empty. */
const getInitials = (firstName: string | null, lastName: string | null) => {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
};

/** Fallback label when the participant name is missing. */
const UNKNOWN_PARTICIPANT_LABEL = 'Participant inconnu';

/** Prefer first+last, then `displayName`, then the unknown-participant fallback. */
const getDisplayName = (
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  displayName?: string | null,
) => {
  const fromNames = `${firstName || ''} ${lastName || ''}`.trim();
  return fromNames || displayName?.trim() || UNKNOWN_PARTICIPANT_LABEL;
};

/** Editable attendance row for a session participant. */
export const AttendanceRow: React.FC<AttendanceRowProps> = ({
  participant,
  value,
  onChange,
  error,
  offeredStatuses,
}) => {
  const statusOptions = attendanceStatusOptions(value.attendanceStatus, offeredStatuses);

  const isTutorOrVolunteer = participant.role === 'tutor' || participant.role === 'volunteer';
  const absenceReasons = isTutorOrVolunteer ? TUTOR_ABSENCE_REASONS : STUDENT_ABSENCE_REASONS;

  const absenceReasonOptions: SelectOption[] = Object.entries(absenceReasons).map(
    ([key, label]) => ({
      value: key,
      label,
    }),
  );

  /** Changing away from absent drops reason / comment. */
  const handleStatusChange = (status: string) => {
    const newValue: ParticipantAttendance = {
      ...value,
      attendanceStatus: status as AttendanceStatus,
    };

    if (status !== 'absent') {
      delete newValue.absenceReason;
      delete newValue.absenceComment;
    }

    onChange(newValue);
  };

  const handleReasonChange = (reason: string) => {
    const newValue: ParticipantAttendance = {
      ...value,
      absenceReason: reason as AbsenceReason,
    };

    onChange(newValue);
  };

  const handleJustificationChange = (justification: string) => {
    onChange({
      ...value,
      absenceComment: justification,
    });
  };

  const roleColors = {
    tutor: 'var(--color-2-nuance-1)',
    student: 'var(--color-1-nuance-1)',
    volunteer: 'var(--color-3-nuance-1)',
  };

  const roleLabels = {
    tutor: getRoleLabel('tutor'),
    student: getRoleLabel('student'),
    volunteer: getRoleLabel('volunteer'),
  };

  const participantUser = participant.user;

  return (
    <div
      className="space-y-3 p-4 rounded-lg border-2 bg-[color-mix(in_srgb,var(--color-1-nuance-3)_5%,transparent)]"
      style={{ borderColor: 'color-mix(in srgb, var(--color-1-nuance-3) 30%, transparent)' }}
    >
      <div className="flex items-center gap-3">
        <Avatar
          src={participantUser?.photoUrl}
          fallback={getInitials(
            participantUser?.firstName ?? null,
            participantUser?.lastName ?? null,
          )}
          size="sm"
        />
        <div className="flex-1">
          <p className="font-medium text-foreground text-sm">
            {getDisplayName(
              participantUser?.firstName,
              participantUser?.lastName,
              participantUser?.displayName,
            )}
          </p>
          <p
            className="text-xs text-muted-foreground"
            style={{ color: roleColors[participant.role] }}
          >
            {roleLabels[participant.role]}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label="Statut de présence"
          placeholder="Sélectionner un statut"
          options={statusOptions}
          value={value.attendanceStatus}
          onChange={handleStatusChange}
          required
          size="sm"
        />

        {value.attendanceStatus === 'absent' && (
          <Select
            label="Motif d'absence"
            placeholder="Sélectionner un motif"
            options={absenceReasonOptions}
            value={value.absenceReason || ''}
            onChange={handleReasonChange}
            required
            size="sm"
            error={error}
          />
        )}
      </div>

      {value.attendanceStatus === 'absent' && value.absenceReason && (
        <Input
          label="Précisez le motif"
          placeholder="Saisissez le motif d'absence"
          value={value.absenceComment || ''}
          onChange={(e) => handleJustificationChange(e.target.value)}
          required
          inputSize="sm"
        />
      )}
    </div>
  );
};
